import { useState, useEffect } from 'react';

/**
 * Force mobile view in dev mode when VITE_FORCE_MOBILE=true (for testing mobile UI on desktop).
 * Only applies when import.meta.env.DEV is true.
 */
const forceMobileInDev =
  import.meta.env.DEV && import.meta.env.VITE_FORCE_MOBILE === 'true';

/**
 * Detect if the device is mobile based on device properties (not viewport size).
 * Desktop users who resize the browser to a small window stay on the regular view.
 */
function detectMobileDevice(): boolean {
  if (forceMobileInDev) return true;
  if (typeof navigator === 'undefined') return false;

  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (nav.userAgentData?.mobile === true) {
    return true;
  }

  const ua = navigator.userAgent.toLowerCase();
  return !!(
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua)
  );
}

/**
 * Hook to detect if the device is mobile (phone/tablet).
 * Based on device properties, not viewport—resizing a desktop browser does not switch to mobile UI.
 * In dev: set VITE_FORCE_MOBILE=true in .env to test mobile layout on desktop.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => detectMobileDevice());

  useEffect(() => {
    setIsMobile(detectMobileDevice());
  }, []);

  return isMobile;
}
